import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { AppLayout } from '../../layouts/AppLayout'
import { PageHeader } from '../../layouts/PageHeader'
import { useUserSession } from '../../../context/UserSessionContext'

interface Permutation {
  grade_name?: string | null
  tech_name?: string | null
  price?: number | null
}

async function fetchAccountOwnerMiscCost(): Promise<number> {
  try {
    const res = await fetch('/api/account-owner-misc-cost')
    if (res.ok) {
      const j = (await res.json()) as { misc_expense_per_hour?: unknown }
      const v = Number(j.misc_expense_per_hour)
      return Number.isFinite(v) && v >= 0 ? v : 0
    }
  } catch {
    /* ignore */
  }
  try {
    const res = await fetch('/config/account_owner_misc_cost.json')
    if (res.ok) {
      const j = (await res.json()) as { misc_expense_per_hour?: unknown }
      const v = Number(j.misc_expense_per_hour)
      return Number.isFinite(v) && v >= 0 ? v : 0
    }
  } catch {
    /* ignore */
  }
  return 0
}

export const RateConfigurePage: React.FC = () => {
  const { isAdmin, googleUser, logAudit } = useUserSession()
  const [permutations, setPermutations] = useState<Permutation[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [viewRatesLocalMap, setViewRatesLocalMap] = useState<
    Record<string, number | string | ''>
  >({})
  const [ratesGridHoverTech, setRatesGridHoverTech] = useState<string | null>(
    null
  )
  const [ratesGridHoverGrade, setRatesGridHoverGrade] = useState<string | null>(
    null
  )
  const [bulkSaving, setBulkSaving] = useState(false)
  const [bulkError, setBulkError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const initialRef = useRef<Record<string, number>>({})

  const [miscExpensePerHour, setMiscExpensePerHour] = useState(0)
  const [miscSavedValue, setMiscSavedValue] = useState(0)
  const [miscSaving, setMiscSaving] = useState(false)
  const [miscError, setMiscError] = useState<string | null>(null)
  const [miscSaveSuccess, setMiscSaveSuccess] = useState(false)

  const gradeTechRateByGradeTech = useMemo(() => {
    const map: Record<string, number> = {}
    ;(permutations || []).forEach(p => {
      const g = (p.grade_name != null && String(p.grade_name).trim()) || ''
      const t = (p.tech_name != null && String(p.tech_name).trim()) || ''
      const price = p.price != null ? Number(p.price) : NaN
      if (g && t && !Number.isNaN(price)) map[`${g}|${t}`] = price
    })
    return map
  }, [permutations])

  const uniqueGrades = useMemo(() => {
    const set = new Set<string>()
    ;(permutations || []).forEach(p => {
      const g = p.grade_name != null ? String(p.grade_name).trim() : ''
      if (g) set.add(g)
    })
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [permutations])

  const uniqueTechs = useMemo(() => {
    const set = new Set<string>()
    ;(permutations || []).forEach(p => {
      const t = p.tech_name != null ? String(p.tech_name).trim() : ''
      if (t) set.add(t)
    })
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [permutations])

  useEffect(() => {
    let cancelled = false
    setLoadError(null)
    setLoading(true)
    Promise.all([
      fetch('/api/grade-tech-permutations').then(res => {
        if (!res.ok) throw new Error(res.statusText || 'Failed to load')
        return res.json()
      }),
      fetchAccountOwnerMiscCost(),
    ])
      .then(([data, misc]) => {
        if (cancelled) return
        setPermutations(Array.isArray(data) ? data : [])
        setMiscExpensePerHour(misc)
        setMiscSavedValue(misc)
      })
      .catch(e => {
        if (cancelled) return
        setLoadError(e instanceof Error ? e.message : 'Failed to load rates')
        setPermutations([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (uniqueGrades.length > 0 || uniqueTechs.length > 0) {
      const copy = { ...gradeTechRateByGradeTech }
      setViewRatesLocalMap(copy)
      initialRef.current = { ...copy }
      setBulkError(null)
    }
  }, [gradeTechRateByGradeTech, uniqueGrades.length, uniqueTechs.length])

  const changedCount = useMemo(() => {
    const initial = initialRef.current
    return Object.keys(viewRatesLocalMap).filter(k => {
      const a = viewRatesLocalMap[k]
      const b = initial[k]
      const na = a !== '' && a != null ? Number(a) : NaN
      const nb = b != null ? Number(b) : NaN
      return (
        na !== nb ||
        (Number.isNaN(na) && !Number.isNaN(nb)) ||
        (!Number.isNaN(na) && Number.isNaN(nb))
      )
    }).length
  }, [viewRatesLocalMap])

  const handleBulkSave = useCallback(async () => {
    if (changedCount === 0) return
    setBulkError(null)
    setBulkSaving(true)
    const initial = initialRef.current
    const updates: { grade_name: string; tech_name: string; price: number }[] =
      []
    Object.keys(viewRatesLocalMap).forEach(k => {
      const [grade, tech] = k.split('|')
      if (!grade || !tech) return
      const val = viewRatesLocalMap[k]
      const num = val !== '' && val != null ? Number(val) : NaN
      const initialNum = initial[k] != null ? Number(initial[k]) : NaN
      if (Number.isNaN(num) || num < 0) return
      if (num === initialNum) return
      updates.push({ grade_name: grade, tech_name: tech, price: num })
    })
    if (updates.length === 0) {
      setBulkSaving(false)
      return
    }
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (googleUser?.email) headers['X-User-Email'] = googleUser.email
      const res = await fetch('/api/grade-tech-permutations/bulk', {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ updates }),
      })
      const data = (await res.json().catch(() => ({}))) as { error?: string }
      if (!res.ok) {
        setBulkSaving(false)
        setBulkError(data.error || `Save failed (${res.status})`)
        return
      }
      const nextInitial: Record<string, number> = {}
      Object.keys(viewRatesLocalMap).forEach(k => {
        const v = viewRatesLocalMap[k]
        const n = v !== '' && v != null ? Number(v) : NaN
        if (!Number.isNaN(n)) nextInitial[k] = n
      })
      initialRef.current = nextInitial
      window.dispatchEvent(new CustomEvent('rateConfigSaved'))
      logAudit('Grade × tech rates saved', `${updates.length} cells`)
      setBulkSaving(false)
      setSaveSuccess(true)
      window.setTimeout(() => setSaveSuccess(false), 3000)
    } catch (e) {
      setBulkSaving(false)
      setBulkError(e instanceof Error ? e.message : 'Network error.')
    }
  }, [viewRatesLocalMap, changedCount, googleUser?.email, logAudit])

  const handleReset = useCallback(() => {
    setViewRatesLocalMap({ ...initialRef.current })
    setBulkError(null)
  }, [])

  const miscDirty = miscExpensePerHour !== miscSavedValue

  const handleMiscSave = useCallback(async () => {
    if (!miscDirty) return
    setMiscError(null)
    setMiscSaving(true)
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (googleUser?.email) headers['X-User-Email'] = googleUser.email
      const res = await fetch('/api/account-owner-misc-cost', {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ misc_expense_per_hour: miscExpensePerHour }),
      })
      const data = (await res.json().catch(() => ({}))) as { error?: string }
      if (!res.ok) {
        setMiscSaving(false)
        setMiscError(data.error || `Save failed (${res.status})`)
        return
      }
      setMiscSavedValue(miscExpensePerHour)
      window.dispatchEvent(new CustomEvent('accountOwnerMiscCostSaved'))
      logAudit(
        'Account Owner misc. cost ($/h) saved',
        String(miscExpensePerHour),
      )
      setMiscSaving(false)
      setMiscSaveSuccess(true)
      window.setTimeout(() => setMiscSaveSuccess(false), 3000)
    } catch (e) {
      setMiscSaving(false)
      setMiscError(e instanceof Error ? e.message : 'Network error.')
    }
  }, [
    miscDirty,
    miscExpensePerHour,
    googleUser?.email,
    logAudit,
  ])

  if (!isAdmin) {
    return (
      <AppLayout>
        <PageHeader
          title="Rate configure"
          subtitle="Technology × grade billing rates ($/hr)."
        />
        <div className="p-6 max-w-lg">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-950">
            <p className="font-semibold">Access denied</p>
            <p className="mt-1 text-amber-900/90">
              Only administrators can edit grade × technology rates.
            </p>
          </div>
        </div>
      </AppLayout>
    )
  }

  const minTableWidth = 140 + uniqueGrades.length * 88

  return (
    <AppLayout>
      <PageHeader
        title="Rate configure"
        subtitle="Technology × Grade matrix ($/hr). Account Owner Portfolio misc. cost is saved below."
        filters={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {changedCount > 0 && (
              <span className="text-xs font-medium text-amber-700">
                {changedCount} unsaved
              </span>
            )}
            <button
              type="button"
              onClick={handleReset}
              disabled={changedCount === 0 || bulkSaving}
              className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => void handleBulkSave()}
              disabled={changedCount === 0 || bulkSaving}
              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
            >
              {bulkSaving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        }
      />

      <div className="p-6">
        {saveSuccess && (
          <p className="mb-3 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
            Saved. Other tabs can listen for <code className="text-xs">rateConfigSaved</code> to refresh.
          </p>
        )}
        {loadError && (
          <p className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-2">
            {loadError}
          </p>
        )}
        {bulkError && (
          <p className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-2">
            {bulkError}
          </p>
        )}

        <div className="mb-3 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 justify-between">
            <div className="min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0">
              <h3 className="text-[11px] font-semibold text-slate-800 leading-none">
                Account Owner Portfolio — misc. cost ($/h)
              </h3>
              <span className="text-[10px] text-slate-500 leading-tight">
                Subtracted from grade × tech cost rate before × billable hours.
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 shrink-0">
              {miscDirty && (
                <span className="text-[10px] font-medium text-amber-700">Unsaved</span>
              )}
              <label className="flex items-center gap-1 text-[10px] text-slate-600">
                <span className="font-medium whitespace-nowrap">Misc. ($/h)</span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={miscExpensePerHour}
                  onChange={e => {
                    const v = e.target.value
                    if (v === '') {
                      setMiscExpensePerHour(0)
                      return
                    }
                    const n = parseFloat(v)
                    setMiscExpensePerHour(
                      Number.isFinite(n) && n >= 0 ? n : 0,
                    )
                  }}
                  disabled={loading}
                  className="w-[4.5rem] py-0.5 px-1.5 text-[11px] font-medium text-slate-800 bg-slate-50 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 tabular-nums disabled:opacity-50"
                />
              </label>
              <button
                type="button"
                onClick={() => {
                  setMiscExpensePerHour(miscSavedValue)
                  setMiscError(null)
                }}
                disabled={!miscDirty || miscSaving || loading}
                className="text-[10px] font-medium px-2 py-0.5 rounded border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => void handleMiscSave()}
                disabled={!miscDirty || miscSaving || loading}
                className="text-[10px] font-medium px-2 py-0.5 rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40"
              >
                {miscSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
          {miscError && (
            <p className="mt-1 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5">
              {miscError}
            </p>
          )}
          {miscSaveSuccess && (
            <p className="mt-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
              Saved — Account Owner Portfolio will refresh.
            </p>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-12rem)] p-2">
            {loading ? (
              <p className="text-slate-500 text-sm py-12 text-center">
                Loading rates…
              </p>
            ) : uniqueGrades.length > 0 && uniqueTechs.length > 0 ? (
              <table
                className="text-xs border-collapse"
                style={{ minWidth: minTableWidth }}
                onMouseLeave={() => {
                  setRatesGridHoverTech(null)
                  setRatesGridHoverGrade(null)
                }}
              >
                <thead className="sticky top-0 z-20 shadow-sm">
                  <tr className="border-b border-slate-200 bg-slate-100">
                    <th className="text-left py-2 px-2 text-slate-700 font-semibold sticky left-0 z-30 bg-slate-100 w-[140px] max-w-[180px] text-[12px] border-r border-slate-200">
                      Tech \ Grade
                    </th>
                    {uniqueGrades.map(g => (
                      <th
                        key={g}
                        className={`text-center py-2 px-1 text-slate-700 font-semibold border-r border-slate-100 text-[11px] min-w-[76px] ${
                          ratesGridHoverGrade === g ? 'bg-sky-100' : ''
                        }`}
                        title={g}
                        onMouseEnter={() => setRatesGridHoverGrade(g)}
                        onMouseLeave={() => setRatesGridHoverGrade(null)}
                      >
                        <span className="block truncate max-w-[84px]" title={g}>
                          {g}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {uniqueTechs.map(tech => (
                    <tr
                      key={tech}
                      className={`border-b border-slate-100 ${
                        ratesGridHoverTech === tech ? 'bg-sky-50/80' : ''
                      }`}
                    >
                      <th
                        className={`text-left py-1 px-2 text-slate-800 font-medium bg-white sticky left-0 z-10 text-[12px] border-r border-slate-200 max-w-[200px] ${
                          ratesGridHoverTech === tech ? 'bg-sky-50' : ''
                        }`}
                        onMouseEnter={() => setRatesGridHoverTech(tech)}
                        onMouseLeave={() => setRatesGridHoverTech(null)}
                      >
                        <span className="truncate block" title={tech}>
                          {tech}
                        </span>
                      </th>
                      {uniqueGrades.map(grade => {
                        const key = `${grade}|${tech}`
                        const cellValue =
                          viewRatesLocalMap[key] ??
                          gradeTechRateByGradeTech[key] ??
                          ''
                        const isCellHovered =
                          ratesGridHoverTech === tech &&
                          ratesGridHoverGrade === grade
                        const isRowOrColHovered =
                          ratesGridHoverTech === tech ||
                          ratesGridHoverGrade === grade
                        return (
                          <td
                            key={grade}
                            className={`py-0.5 px-0.5 text-center border-r border-slate-100 ${
                              isCellHovered
                                ? 'bg-sky-200/70 ring-1 ring-sky-400'
                                : isRowOrColHovered
                                  ? 'bg-sky-50/50'
                                  : ''
                            }`}
                            onMouseEnter={() => {
                              setRatesGridHoverTech(tech)
                              setRatesGridHoverGrade(grade)
                            }}
                          >
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={
                                cellValue === '' || cellValue == null
                                  ? ''
                                  : Number(cellValue)
                              }
                              onChange={e => {
                                const v = e.target.value
                                setViewRatesLocalMap(prev => ({
                                  ...prev,
                                  [key]: v === '' ? '' : v,
                                }))
                              }}
                              className="w-full min-w-0 py-1.5 px-0.5 text-center text-xs font-medium text-emerald-800 bg-slate-50 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-slate-500 text-sm py-12 text-center">
                No rate data loaded.
              </p>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

export default RateConfigurePage
